import { Component } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Top-level error boundary — a render crash (or a lazy route chunk that
 * fails to load, e.g. a stale tab after a deploy) shows a friendly
 * "Something went wrong — reload" page instead of a blank screen.
 *
 * Errors are logged to the console. `resetKey` clears the error when it
 * changes, so navigating to another route recovers without a reload.
 */
class ErrorBoundaryInner extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Render error:', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-24 text-center"
        data-testid="error-boundary"
      >
        <h1 className="text-2xl font-semibold text-gray-900 mb-3">Something went wrong</h1>
        <p className="text-gray-500 max-w-sm mb-8 text-sm leading-relaxed">
          This page hit an unexpected error. Reloading usually fixes it.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-wine-600 text-white text-sm font-semibold hover:bg-wine-700 transition"
            data-testid="error-boundary-reload"
          >
            Reload page
          </button>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-300 text-gray-700 text-sm font-semibold hover:border-wine-400 hover:text-wine-700 transition"
          >
            Go to homepage
          </a>
        </div>
      </div>
    );
  }
}

/** Must render inside <BrowserRouter>; resets on route change. */
const ErrorBoundary = ({ children }) => {
  const { pathname } = useLocation();
  return <ErrorBoundaryInner resetKey={pathname}>{children}</ErrorBoundaryInner>;
};

export default ErrorBoundary;
